export interface copilotFlow {
  messageFrom: string;
  recordType: RecordType;
  message?: any;
  interactions?: any;
  links?: copilotAsset[];
  images?: copilotAsset[];
  process?: copilotAsset[];
  sentimentScore?: Sentiment;
  emailTemplate?: any;
}

export enum RecordType {
  Sentiment = 'sentiment',
  SuggestionArticle = 'suggestionArticle',
  Compliance = 'compliance',
  SalesOffer = 'salesOffer',
  NBR = 'nbr',
  CustomerReply = 'customerReply',
  BehavioralGuidance = 'behavioralGuidance',
  ElevationData = 'elevationData',
}

export interface copilotAsset {
  webLinkLabel?: string;
  webLinkURL?: string;
  imgSrc?: string;
  processTitle?: string;
  processDescription?: string;
}

export enum Sentiment {
  NEGATIVE = 0,
  NEUTRAL = 1,
  POSITIVE = 2,
}

export const customCopilotVoiceTemplate = {
  journeyHistory: {
    journeySummaryText:
      'Liam has previously completed travel booking on the website, with positive sentiment.',
    interactions: [
      {
        CustomerName: 'Liam Davis',
        InteractionDateTime: '2023-7-20 10:22:00.000',
        InteractionDurationInSeconds: 172,
        InteractionOrigin: 'Customer Service',
        InteractionReason: 'Compare Plans',
        InteractionSentiment: 'Neutral',
        InteractionSummary:
          'The customer logged into the online portal to compare various travel plans',
        InteractionType: 'WebSiteVisit',
        IsResolved: true,
        StepsTakenDuringCall: 'Compare Plans',
      },
      {
        InteractionType: 'WebSiteVisit',
        InteractionReason: 'Book Travel Plan',
        InteractionDateTime: '2023-6-02 8:04:00.000',
        InteractionSentiment: 'Neutral',
        IsResolved: true,
        InteractionOrigin: 'Customer Service',
        StepsTakenDuringCall: 'Book Travel Plan',
        InteractionDurationInSeconds: 170,
        CustomerName: 'Liam Davis',
        InteractionSummary:
          'The customer logged into the online portal to complete a travel booking to Europe',
      },
    ],
  },
  rtSummary:
    'Liam would like to book a trip to Iceland this summer that involves hiking and being in nature.',
  finalAutoSummary:
    'Customer wanted help booking his trip to Iceland and was overwhelmed by activity options. Agent created a 7-day itinerary for his trip that covered all his destinations of choice and helped him pick an appropriate time to travel. The Agent sent the itinerary to the customer via email. Customer was satisfied with the resolution of the call.',
  agentTransferSummary:
    'Liam Called into customer support looking to book a trip to iceland',
  copilotAgentAndCustomerVoiceData: [
    {
      messageFrom: 'copilot',
      recordType: RecordType.NBR,
      message: [
        `Hello and thank you for calling OneTravel. My name is Daniel. How can I help you today?`,
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.Sentiment,
      message: [
        {
          description:
            'Customer wants to plan a trip but is overwhelmed by all the activity options',
        },
      ],
      sentimentScore: Sentiment.NEGATIVE,
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.NBR,
      message: [
        `I'm sorry to hear that. I'd love to help you plan your trip. Can you please tell me about what you'd like to do while you're in Iceland? What are some of your interests?`,
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.SuggestionArticle,
      message: [
        {
          kbAnswers: {
            title: 'Planning your trip to Iceland',
            description:
              `Suggested 7-Day Iceland Itinerary:\n
Day 1: Arrival in Reykjavik, relax at the Blue Lagoon.\n
Day 2: Take a Golden Circle tour to see Þingvellir National Park, Geysir, and Gullfoss waterfall.\n
Day 3: Explore the South Coast, visiting Seljalandsfoss and Skogafoss waterfalls, and the black sand beaches of Reynisfjara.\n
Day 4: Head to Vatnajökull National Park for glacier hiking and a visit to Jokulsarlon Glacier Lagoon.\n
Day 5: Continue exploring the East Fjords, known for their stunning landscapes and charming fishing villages.\n
Day 6: Return to Reykjavik, visit Hallgrimskirkja church and explore the city.\n
Day 7: Depart from Reykjavik.`,
            internalInfo: [
              {
                type: 'internalInfo',
                id: 1,
                title: 'Iceland Visa Status',
                description:
                  'If a client is outside the U.S. let them know that they may require a visa from iceland and should plan their vacation at least two months out. ',
                icons: [],
                isItemSelected: false,
                isItemSent: false,
              },
            ],
          },
          kbInternalUse: {},
          kbLinks: [
            {
              text: 'Visiting Vatnajökull National Park',
              link: 'https://www.google.com/',
            },
            {
              text: 'Jokulsarlon Glacier Lagoon',
              link: 'https://www.google.com/',
            },
            {
              text: '7 Days in Iceland',
              link: 'https://www.google.com/',
            },
          ],
          kbImages: [
            {
              name: 'iceland1',
              imageUrl: './assets/iceland1.png',
            },
            {
              name: 'iceland2',
              imageUrl: './assets/iceland2.png',
            },
          ],
          kbProcessSteps: [
            {
              title: 'Enter your interests into oneTravel.com',
              description: "Let us know what you'd like to do ",
            },
            {
              title: 'View/Accept Suggested Itinerary',
              description:
                'Review the details and make changes as you see fit based on our recommendations ',
            },
            {
              title: 'Make Reservations',
              description:
                'Click the button, pay and reserve, then we will take it from there',
            },
          ],
        },
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.NBR,
      message: [
        `There's so much to see in Iceland. You're going to love all the natural scenary and national parks.`,
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.Sentiment,
      message: [
        {
          description:
            'Customer is feeling better about the trip that they are planning and the time of year',
        },
      ],

      sentimentScore: Sentiment.NEUTRAL,
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.NBR,
      message: [
        `Excellent! Can you remind me when you were planning on booking your trip to iceland?`,
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.SuggestionArticle,
      message: [
        {
          kbAnswers: {
            title: 'Weather in Iceland in June',
            description:
              'June is the first of three summer months in Iceland along with July and August. The summer in Iceland is beautiful, the lush is in full bloom and there is so much to do as we have daylight 24/7. The average temperature in June is 9°C (48°F) but it can go as high as 15°C (59°F) or more.',
          },
          kbInternalUse: {},
          kbLinks: [
            {
              text: 'Visiting Vatnajökull National Park',
              link: 'https://www.google.com/',
            },
            {
              text: 'Jokulsarlon Glacier Lagoon',
              link: 'https://www.google.com/',
            },
            {
              text: '7 Days in Iceland',
              link: 'https://www.google.com/',
            },
          ],
          kbImages: [
            {
              imageUrl: './assets/iceland1.png',
            },
          ],
          kbProcessSteps: [],
        },
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.NBR,
      message: [
        `June looks like a perfect time to visit. I'm going to send that itinerary right over to you.`,
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.Sentiment,
      message: [
        {
          description:
            'The customer feels satisfied with the resolution of their issue.',
        },
      ],

      sentimentScore: Sentiment.POSITIVE,
    },
  ],
};
